import type { APIReferenceAttributes, ArdoqId } from '@ardoq/api-types';
import { isTruthy } from '@ardoq/common-helpers';
import { COMPONENT_ID_ATTRIBUTE } from '@ardoq/global-consts';
import type {
  CompElementExtension,
  IntegrationElementExtension,
} from 'aqTypes';
import type { ComponentsState } from 'streams/components/components$';
import type { StreamCollection } from 'streams/utils/streamUtils';
import { findAncestorWithProperty } from 'utils/domUtils';

/** The ids a DOM element carries, whether or not they are loaded. */
export type ModelElementIds = {
  componentIds: ArdoqId[];
  referenceIds: ArdoqId[];
};

export type ModelElement = {
  entityType: 'component' | 'reference';
  id: ArdoqId;
};

export const getComponentIds = (target: Element): ArdoqId[] => {
  const elementWithComp = findAncestorWithProperty(target, 'comp') as
    | (Element & CompElementExtension)
    | null;
  if (elementWithComp) {
    return [elementWithComp.comp.id];
  }

  const componentDataset = target.closest<HTMLElement>(
    `[data-global-handler-id].component, [${COMPONENT_ID_ATTRIBUTE}].component`
  )?.dataset;
  const componentId =
    componentDataset?.globalHandlerId || componentDataset?.componentId;
  return componentId ? [componentId] : [];
};

export const getReferenceIds = (target: Element): ArdoqId[] => {
  const integration = (target as Element & IntegrationElementExtension)
    .integration; // this is still used on edge labels.
  if (integration) {
    return [integration.id];
  }

  const dataset = target.closest<HTMLElement | SVGElement>(
    '[data-global-handler-id].integration, [data-reference-model-ids].integration'
  )?.dataset;
  if (!dataset) {
    return [];
  }
  return [
    dataset.globalHandlerId,
    ...(dataset.referenceModelIds?.split(',') ?? []),
  ].filter(isTruthy);
};

export const getModelElementIds = (target: Element): ModelElementIds => ({
  componentIds: getComponentIds(target),
  referenceIds: getReferenceIds(target),
});

/**
 * The first loaded component among the ids, or else the first loaded
 * reference.
 */
export const toLoadedModelElement = (
  { componentIds, referenceIds }: ModelElementIds,
  components: ComponentsState,
  references: StreamCollection<APIReferenceAttributes>
): ModelElement | null => {
  const componentId = componentIds.find(id => Boolean(components.byId[id]));
  if (componentId) {
    return { entityType: 'component', id: componentId };
  }
  const referenceId = referenceIds.find(id => Boolean(references.byId[id]));
  if (referenceId) {
    return { entityType: 'reference', id: referenceId };
  }
  return null;
};
